import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Zap, Network, Cpu, ShieldCheck, BarChart3, Phone, MessagesSquare, Workflow, CalendarCheck, Building2, Briefcase, Wallet, Scale, HeartPulse, TrendingUp, PiggyBank, Landmark, Home as HomeIcon, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import DataFlowDiagram from "@/components/marketing/DataFlowDiagram";
import DashboardPreview from "@/components/marketing/DashboardPreview";
import TrustBanner from "@/components/marketing/TrustBanner";
import CustomerStories from "@/components/marketing/CustomerStories";
import PartnerOfTheMonth from "@/components/marketing/PartnerOfTheMonth";
import PilotOfferStrip from "@/components/marketing/PilotOfferStrip";
import BenchmarkBanner from "@/components/marketing/BenchmarkBanner";
import CodeRain from "@/components/marketing/CodeRain";
import { useCodeRain } from "@/lib/siteEffects";

const SECTORS = [
  { slug: "mortgages", name: "Mortgages", Icon: HomeIcon },
  { slug: "business-finance", name: "Business Finance", Icon: Briefcase },
  { slug: "debt-help", name: "Debt Help", Icon: ShieldCheck },
  { slug: "payday-loans", name: "Payday & Short-Term", Icon: Wallet },
  { slug: "claims-management", name: "Claims Management", Icon: Scale },
  { slug: "life-insurance", name: "Life Insurance", Icon: HeartPulse },
  { slug: "investor-leads", name: "Investor Leads", Icon: TrendingUp },
  { slug: "wealth-management", name: "Wealth Management", Icon: PiggyBank },
  { slug: "commercial-finance", name: "Commercial Finance", Icon: Building2 },
  { slug: "property-investment", name: "Property Investment", Icon: Landmark },
];

export default function Home() {
  const rain = useCodeRain("home");
  const navyRain = useCodeRain("home", "dark");
  return (
    <div className="bg-white">
      {/* HERO — one code field runs the whole top of the page, behind everything */}
      <section className="relative overflow-hidden border-b border-slate-200/80">
        <div className="absolute inset-0 dot-grid dot-grid-fade opacity-80" />
        <div className="absolute top-24 left-1/2 -translate-x-1/2 w-[680px] h-[680px] rounded-full bg-primary/5 blur-3xl" />
        {rain.on && <CodeRain variant="light" intensity={rain.intensity} />}
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 lg:pt-28 pb-12 lg:pb-16">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Badge variant="outline" className="rounded-full border-slate-300 bg-white/70 backdrop-blur text-slate-700 px-3 py-1" data-testid="hero-badge">
                <span className="size-1.5 rounded-full bg-emerald-500 mr-2 animate-pulse" />
                Now routing 240,000+ financial enquiries / month
              </Badge>
              <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-[-0.04em] text-slate-900 leading-[0.95]">
                Infrastructure for<br />
                <span className="gradient-text">financial growth.</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
                Connexis is the operating layer behind modern financial brands — high-intent customer acquisition, AI voice & chat operations, and enterprise routing infrastructure, delivered as one platform.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/buy-leads"><Button data-testid="hero-cta-primary" className="rounded-full px-5 h-11 text-[15px]">Buy exclusive leads <ArrowUpRight className="size-4" /></Button></Link>
                <Link to="/solutions"><Button data-testid="hero-cta-secondary" variant="outline" className="rounded-full px-5 h-11 text-[15px] border-slate-300">Explore the platform <ArrowRight className="size-4" /></Button></Link>
              </div>

              {/* Trusted-by mini strip (iter56) */}
              <div className="mt-8" data-testid="hero-trusted-by">
                <div className="text-[10.5px] uppercase tracking-[0.16em] text-slate-400 font-medium mb-2.5">Trusted by leading UK financial brands</div>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[12.5px] font-medium text-slate-500">
                  <span>Aspect Capital</span>
                  <span className="size-1 rounded-full bg-slate-300" />
                  <span>Beacon Finance</span>
                  <span className="size-1 rounded-full bg-slate-300" />
                  <span>Helix Wealth</span>
                  <span className="size-1 rounded-full bg-slate-300" />
                  <span>Northforth Lending</span>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-2"><CheckCircle2 className="size-3.5 text-primary" /> FCA-aware compliance flows</span>
                <span className="inline-flex items-center gap-2"><CheckCircle2 className="size-3.5 text-primary" /> 99.99% routing uptime</span>
                <span className="inline-flex items-center gap-2"><CheckCircle2 className="size-3.5 text-primary" /> Sub-second lead delivery</span>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative">
                <DashboardPreview />
              </div>
            </div>
          </div>

          {/* Diagram strip — glass, so the field behind it reads through without touching the wires */}
          <div className="relative mt-14">
            <div className="relative rounded-2xl border border-slate-200 bg-white/70 ring-1 ring-white/50 shadow-soft backdrop-blur-[10px] p-4 sm:p-8">
              <div className="flex items-center justify-between gap-3 mb-4">
                <span className="text-xs font-semibold tracking-widest text-slate-500 uppercase">Live routing topology</span>
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-600">
                    <span className="relative flex items-center justify-center size-1.5">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex rounded-full size-1.5 bg-emerald-500" />
                    </span>
                    Routing live
                  </span>
                  <span className="hidden sm:inline text-xs text-slate-500 font-mono">v2.4 · 98.6ms median</span>
                </div>
              </div>
              {/* The SVG scales with its viewBox, so on a phone it is the desktop diagram at a smaller size —
                  the whole path in view, nothing to swipe or drag. */}
              <div className="w-full aspect-[640/410] sm:aspect-auto sm:h-[420px]" data-testid="home-topology-frame">
                <DataFlowDiagram />
              </div>
            </div>

            {/* Logo strip — dual marquee, opposite directions */}
            <div className="relative mt-10">
              <p className="text-center text-xs uppercase tracking-widest text-slate-500">Trusted by financial operators across the UK & EU</p>
              <div className="mt-5 overflow-hidden scrollbar-hide" data-testid="logo-marquee-row-1">
                <div className="marquee-track flex items-center gap-12 opacity-70">
                  {["Northbridge", "Aspect Capital", "Pillar Life", "Beacon Finance", "Helix Claims", "Resolve Debt", "Sterling Mortgage", "Atlas Wealth", "Marlow Property", "Crown Finance"].concat(["Northbridge", "Aspect Capital", "Pillar Life", "Beacon Finance", "Helix Claims", "Resolve Debt", "Sterling Mortgage", "Atlas Wealth", "Marlow Property", "Crown Finance"]).map((n, i) => (
                    <span key={i} className="text-sm font-semibold tracking-tight text-slate-500 whitespace-nowrap">{n}</span>
                  ))}
                </div>
              </div>
              <div className="mt-3 overflow-hidden scrollbar-hide" data-testid="logo-marquee-row-2">
                <div className="marquee-track-reverse flex items-center gap-12 opacity-55">
                  {["Marlborough Capital", "Westgate Wealth", "Halford & Co.", "Brookfield Lending", "Citadel Mortgage", "Norwood Insurance", "Pendle Finance", "Wessex Partners", "Hartfield Brokers", "Avalon Credit"].concat(["Marlborough Capital", "Westgate Wealth", "Halford & Co.", "Brookfield Lending", "Citadel Mortgage", "Norwood Insurance", "Pendle Finance", "Wessex Partners", "Hartfield Brokers", "Avalon Credit"]).map((n, i) => (
                    <span key={i} className="text-sm font-semibold tracking-tight text-slate-400 whitespace-nowrap">{n}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BANNER — live MongoDB-backed stats */}
      <TrustBanner />

      {/* FUNDED PILOT OFFER */}
      <PilotOfferStrip />

      {/* THREE PILLARS */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">The platform</p>
            <h2 className="mt-3 text-4xl sm:text-5xl tracking-[-0.03em] font-medium text-slate-900">Three pillars. One operational layer.</h2>
            <p className="mt-4 text-slate-600 text-lg">Acquisition, automation and infrastructure — operated under a single API, a single dashboard, and a single accountable team.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { Icon: Zap, title: "Customer acquisition", desc: "High-intent enquiries across 10 financial sectors. Shared, exclusive, live transfers, AI-qualified and appointment-ready.", points: ["Real-time delivery (≤1s)", "Ping/post & direct-post", "TrustedForm + GDPR"] },
              { Icon: Cpu, title: "AI operational systems", desc: "Voice agents, omnichannel chat, WhatsApp automation, appointment booking and compliance-scripted workflows.", points: ["Sub-300ms voice latency", "Multilingual support", "CRM-native handoffs"] },
              { Icon: Network, title: "Enterprise infrastructure", desc: "Client dashboards, ping trees, buyer routing, billing, affiliate portals and reporting — purpose-built for finance.", points: ["Role-based portals", "Compliance audit log", "API-first delivery"] },
            ].map((p, i) => (
              <div key={i} className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 tilt-hover hover:shadow-float" data-testid={`pillar-card-${i}`}>
                <div className="absolute -right-12 -top-12 size-40 rounded-full bg-primary/5 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <span className="inline-grid place-items-center size-10 rounded-lg bg-slate-900 text-white">
                  <p.Icon className="size-5" strokeWidth={1.6} />
                </span>
                <h3 className="mt-6 text-xl font-medium tracking-tight text-slate-900">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                <ul className="mt-5 space-y-1.5">
                  {p.points.map((pt, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="size-4 text-primary" /> {pt}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section className="py-24 sm:py-32 bg-slate-50/60 border-y border-slate-200/70">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">Sectors</p>
              <h2 className="mt-3 text-4xl sm:text-5xl tracking-[-0.03em] font-medium text-slate-900">Built for every corner of financial services.</h2>
            </div>
            <Link to="/sectors"><Button variant="ghost" className="rounded-full" data-testid="sectors-view-all">View all sectors <ArrowRight className="size-4" /></Button></Link>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {SECTORS.map((s, i) => (
              <Link key={i} to={`/sectors/${s.slug}`} className="group rounded-xl border border-slate-200 bg-white p-4 sm:p-5 hover:border-primary/40 hover:shadow-soft transition-all" data-testid={`sector-tile-${s.slug}`}>
                <s.Icon className="size-5 text-slate-700 group-hover:text-primary transition-colors" strokeWidth={1.6} />
                <div className="mt-4 sm:mt-6 text-[13.5px] sm:text-[15px] font-medium text-slate-900 leading-snug">{s.name}</div>
                <div className="mt-1 inline-flex items-center text-xs text-slate-500 group-hover:text-primary">Explore <ArrowRight className="size-3 ml-1" /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BENCHMARK REPORT BANNER */}
      <BenchmarkBanner />

      {/* AI SYSTEMS */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">AI operations</p>
            <h2 className="mt-3 text-4xl sm:text-5xl tracking-[-0.03em] font-medium text-slate-900">AI systems built for financial operations.</h2>
            <p className="mt-4 text-slate-600 text-lg">Voice, chat, WhatsApp and omnichannel — engineered with sub-second latency and FCA-compliant scripting. Connexis AI lives natively inside your routing and CRM.</p>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {[
                { Icon: Phone, l: "AI Voice Agents" },
                { Icon: MessagesSquare, l: "Conversational AI" },
                { Icon: Workflow, l: "Omnichannel Router" },
                { Icon: CalendarCheck, l: "Appointment Booker" },
              ].map((x, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-3">
                  <span className="grid place-items-center size-8 rounded-md bg-slate-900 text-white"><x.Icon className="size-4" strokeWidth={1.6} /></span>
                  <span className="text-sm font-medium text-slate-800">{x.l}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 flex gap-3">
              <Link to="/ai-systems"><Button variant="outline" className="rounded-full border-slate-300" data-testid="ai-explore-btn">Explore AI systems <ArrowRight className="size-4" /></Button></Link>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-float p-6">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold tracking-widest uppercase text-slate-500">Voice transcript</span>
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-500"><span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live</span>
              </div>
              <div className="mt-4 space-y-3">
                {[
                  { who: "AI", text: "Hello, you're through to Connexis on behalf of Northbridge — I understand you're enquiring about a remortgage?", t: "00:00" },
                  { who: "Caller", text: "Yes, looking to switch from a 4.9% fix.", t: "00:04" },
                  { who: "AI", text: "Got it. Is the property in England, Scotland or Wales — and what's the outstanding balance, roughly?", t: "00:07" },
                  { who: "Caller", text: "England. About £218,000.", t: "00:12" },
                ].map((m, i) => (
                  <div key={i} className="flex gap-3">
                    <span className={`shrink-0 mt-0.5 inline-flex h-6 w-12 items-center justify-center rounded-md text-[10px] font-semibold ${m.who === "AI" ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-600"}`}>{m.who}</span>
                    <div className="flex-1">
                      <p className="text-sm text-slate-800 leading-relaxed">{m.text}</p>
                      <span className="text-[10px] font-mono text-slate-400">{m.t}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 grid grid-cols-3 gap-3">
                <Stat l="Latency" v="284ms" />
                <Stat l="Qualified" v="73.1%" />
                <Stat l="Booked" v="32%" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* METRICS BAND — the code field reads white on navy here */}
      <section className="relative overflow-hidden py-14 sm:py-20 bg-slate-900 text-white">
        {navyRain.on && <CodeRain variant="dark" intensity={navyRain.intensity} />}
        {/* Two rows of two on a phone, one line of four from md. Each figure sits on its own
            faint pane below md so a falling digit can never print on top of a label. */}
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4 md:gap-8">
          {[
            { v: "240,000+", l: "Enquiries routed / mo" },
            { v: "£9.6M", l: "Annualised buyer spend" },
            { v: "98.6ms", l: "Median route latency" },
            { v: "99.99%", l: "Routing uptime SLA" },
          ].map((m, i) => (
            <div key={i} data-testid={`home-metric-${i}`}
                 className="min-w-0 rounded-xl bg-slate-900/60 ring-1 ring-white/[0.06] px-3.5 py-4 backdrop-blur-[2px] md:rounded-none md:bg-transparent md:p-0 md:ring-0 md:backdrop-blur-none">
              <div className="text-[30px] sm:text-[38px] md:text-5xl font-semibold tracking-[-0.035em] leading-none tabular-nums">{m.v}</div>
              <div className="mt-2 text-[12.5px] md:text-sm text-slate-400 leading-snug">{m.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIAL */}
      <CustomerStories
        heading="Live numbers, live replays."
        subheading="Six most active sectors right now — each card pulls real Connexis platform telemetry, refreshed every page load."
        maxCards={6}
      />

      {/* PARTNER OF THE MONTH — public social proof from /api/public/affiliate-leaderboard */}
      <PartnerOfTheMonth />

      <section className="py-24 sm:py-32 bg-slate-50/40 border-t border-slate-200">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <Sparkles className="size-5 text-primary mx-auto" />
          <blockquote className="mt-6 text-3xl sm:text-4xl tracking-[-0.02em] font-medium text-slate-900 leading-snug">
            "Connexis is the only partner we've worked with where the routing layer, the AI qualification, and the buyer portal all feel like one product. It changed how we operate."
          </blockquote>
          <div className="mt-8 text-sm text-slate-600">
            <span className="font-semibold text-slate-900">Sarah Chen</span> — Head of Acquisition, Northbridge Mortgage Co.
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-slate-900 text-white p-12 sm:p-16">
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)", backgroundSize: "22px 22px" }} />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-4xl sm:text-5xl tracking-[-0.03em] font-medium">Ready to operate like infrastructure?</h3>
                <p className="mt-4 text-slate-300 max-w-md">A 30-minute strategy call with our team will map your acquisition stack, AI surface area and routing economics.</p>
              </div>
              <div className="flex md:justify-end gap-3 flex-wrap">
                <Link to="/book-demo"><Button data-testid="cta-book-demo" className="rounded-full px-5 h-11 bg-white text-slate-900 hover:bg-slate-100">Book strategy call <ArrowUpRight className="size-4" /></Button></Link>
                <Link to="/pricing"><Button variant="outline" className="rounded-full px-5 h-11 border-white/30 text-white hover:bg-white/10" data-testid="cta-see-pricing">See pricing <ArrowRight className="size-4" /></Button></Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ l, v }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{l}</div>
      <div className="mt-1 text-base font-semibold text-slate-900 tabular-nums">{v}</div>
    </div>
  );
}
